// [#24] Client-side pre-flight for photo uploads.
//
// Every photo the app uploads goes through `POST /upload/photos` (or
// `/upload/evidence`), which rejects on size and on file type. Until this
// module existed nothing on the client checked either, so a straight-from-the-
// camera phone photo was uploaded IN FULL over mobile data and only then
// refused. This module lets the three pickers refuse it before a byte leaves
// the device.
//
// THIS FILE MIRRORS THE SERVER. It must never be STRICTER than the server, or
// it will block uploads the server would have accepted. Every value below is
// copied from `server/upload/uploadRoutes.js`, which is the only authority:
//
//   :99  const MAX_FILE_BYTES = 5 * 1024 * 1024;
//   :97  const ALLOWED_EXT_REGEX  = /jpeg|jpg|png|webp/;
//   :98  const ALLOWED_MIME_REGEX = /jpeg|jpg|png|webp/;
//   :106-116  fileFilter -- accepts only when BOTH ext AND mime match
//   :113 "Unsupported file type. Allowed: jpeg, png, webp"
//   :128 "File too large (max 5 MB)"
//
// The two message strings are the SERVER'S OWN, verbatim, deliberately: the
// user sees exactly the sentence they would have seen after the round trip, so
// this introduces no new copy to review.
//
// THREE CALLERS, BUT FOUR UPLOADERS — the fourth is excluded on purpose.
// `users/service/userApi.js` `uploadProfilePhoto` posts to the same
// `/upload/photos` under the same limits, but the avatar path canvas
// re-encodes the picked image to a fresh `new File([blob], "avatar.jpg",
// { type: "image/jpeg" })` before uploading. Its name and mime are therefore
// always valid, and its size is the size of the CROP, not of the pick. Running
// `photoRejection` on the file the user PICKED there would reject an 8 MB
// original that becomes a tiny crop the server would have accepted — a false
// rejection, which is the one failure mode this module must not have.

export const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

// Deliberately the server's unanchored regex, applied to BOTH the extension
// and the mime type, exactly as `fileFilter` does. Note `jpg` is accepted as
// an extension, so `.jpg` files must pass.
export const ALLOWED_PHOTO_REGEX = /jpeg|jpg|png|webp/;

export const PHOTO_TOO_LARGE_MESSAGE = 'File too large (max 5 MB)';
export const PHOTO_BAD_TYPE_MESSAGE =
  'Unsupported file type. Allowed: jpeg, png, webp';

// `path.extname` semantics, reimplemented because the client has no `path`:
// the substring from the LAST dot onward, or '' when there is no dot or the
// name begins with one. Lowercased, as the server lowercases before testing.
//
// One honest divergence, left in deliberately: this ignores path separators,
// which `path.extname` honours — `path.extname('a.jpeg/b')` is `''` while this
// returns `'.jpeg/b'`. It is unreachable from a browser `File.name`, which
// carries no directory part, and it errs toward ACCEPTING, so the server still
// has the last word. Recorded rather than silently "fixed" because a stricter
// client is the failure mode this module must not have.
const extensionOf = (name) => {
  const safe = typeof name === 'string' ? name : '';
  const dot = safe.lastIndexOf('.');
  if (dot <= 0) return '';
  return safe.slice(dot).toLowerCase();
};

/**
 * The reason this file would be refused by the server, or null if it would be
 * accepted.
 *
 * Size is compared with `>=`, NOT `>`. busboy stops the stream and raises
 * LIMIT_FILE_SIZE at equality -- `server/node_modules/busboy/lib/types/
 * multipart.js:476` is `if (fileSize === fileSizeLimit)` -- so a file of
 * exactly 5 MB is rejected by the server, and a client using `>` would wave it
 * through and reproduce the very bug this module exists to prevent.
 *
 * @param {File} file
 * @returns {string|null}
 */
export const photoRejection = (file) => {
  if (!file) return null;

  if (typeof file.size === 'number' && file.size >= MAX_PHOTO_BYTES) {
    return PHOTO_TOO_LARGE_MESSAGE;
  }

  const extOk = ALLOWED_PHOTO_REGEX.test(extensionOf(file.name));
  const mimeOk = ALLOWED_PHOTO_REGEX.test(
    typeof file.type === 'string' ? file.type : ''
  );
  if (!(extOk && mimeOk)) return PHOTO_BAD_TYPE_MESSAGE;

  return null;
};

/**
 * The first reason any file in a selection would be refused, or null if the
 * whole selection would be accepted.
 *
 * The whole selection is refused, matching how the three pickers already treat
 * their existing count guard ("Maximum 5 photos allowed") -- it sets an error
 * and adds nothing. This is not a new interaction model.
 *
 * @param {File[]} files
 * @returns {string|null}
 */
export const firstPhotoRejection = (files) => {
  const list = Array.isArray(files) ? files : [];
  for (const file of list) {
    const reason = photoRejection(file);
    if (reason) return reason;
  }
  return null;
};
